import {
  useLazyGetClientConversationTimelineQuery,
  useGetClientSegmentsWithSummaryQuery,
} from "features/clients/detail/api"
import { useCallback, useEffect, useRef, useState } from "react"
import ConversationHistory from "./conversationHistory"
import { mergeAppendedConversationTimeline } from "./conversationTimelineMerge"

const PAGE_SIZE = 10
const ConversationHistorySection = ({ clientId, scoresBreakdown = {} }) => {
  const { data: segmentsApiData } = useGetClientSegmentsWithSummaryQuery({ clientId }, { skip: !clientId })

  const [requestTimelinePage, { isFetching: timelineFetchActive }] = useLazyGetClientConversationTimelineQuery()

  const [segmentFilterSelection, setSegmentFilterSelection] = useState("all")
  const [conversationRows, setConversationRows] = useState([])

  const nextPageIndexRef = useRef(0)
  const acceptedRowTotalRef = useRef(0)
  const mergedLengthRef = useRef(0)
  const requestInFlightRef = useRef(false)

  const resetPagingAndRows = () => {
    nextPageIndexRef.current = 0
    acceptedRowTotalRef.current = 0
    mergedLengthRef.current = 0
    setConversationRows([])
  }

  const loadTimelinePage = useCallback(
    async (segmentChoice, { replaceExistingRows }) => {
      if (!clientId || requestInFlightRef.current) return

      const pageIndex = replaceExistingRows ? 0 : nextPageIndexRef.current
      if (
        !replaceExistingRows &&
        acceptedRowTotalRef.current > 0 &&
        mergedLengthRef.current >= acceptedRowTotalRef.current
      ) {
        return
      }

      requestInFlightRef.current = true
      try {
        const timelineRequest =
          segmentChoice === "all"
            ? { clientId, page: pageIndex, pageLimit: PAGE_SIZE }
            : { clientId, segmentId: segmentChoice, page: pageIndex, pageLimit: PAGE_SIZE }

        const nextEnvelope = await requestTimelinePage(timelineRequest).unwrap()

        acceptedRowTotalRef.current = nextEnvelope?.count
        nextPageIndexRef.current = pageIndex + 1

        setConversationRows(previousRows => {
          const combined = replaceExistingRows
            ? nextEnvelope.segments
            : mergeAppendedConversationTimeline(previousRows, nextEnvelope.segments)
          mergedLengthRef.current = combined.length
          return combined
        })
      } finally {
        requestInFlightRef.current = false
      }
    },
    [clientId, requestTimelinePage],
  )

  useEffect(() => {
    if (!clientId) return
    setSegmentFilterSelection("all")
    resetPagingAndRows()
    loadTimelinePage("all", { replaceExistingRows: true })
  }, [clientId, loadTimelinePage])

  const handleSegmentFilterChange = selectedId => {
    setSegmentFilterSelection(selectedId)
    resetPagingAndRows()
    loadTimelinePage(selectedId, { replaceExistingRows: true })
  }

  const requestNextTimelinePageFromScrollEnd = () => {
    loadTimelinePage(segmentFilterSelection, { replaceExistingRows: false })
  }

  const firstPageLoadingFlag = timelineFetchActive && conversationRows.length === 0
  const additionalPageLoadingFlag = timelineFetchActive && conversationRows.length > 0

  return (
    <ConversationHistory
      conversations={conversationRows}
      segmentOptions={segmentsApiData?.segmentOptions ?? []}
      selectedSegmentId={segmentFilterSelection}
      onSegmentChange={handleSegmentFilterChange}
      loading={firstPageLoadingFlag}
      loadingFurther={additionalPageLoadingFlag}
      onReachEnd={requestNextTimelinePageFromScrollEnd}
      scoresBreakdown={scoresBreakdown}
    />
  )
}

export default ConversationHistorySection
